import {faTimes} from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import {useState, useCallback} from 'react';
import styled, {css} from 'styled-components';

import {ComposedIcon} from '../../components/Icon/ComposedIcon';
import {Icon} from '../../components/Icon/Icon';
import {scToTwColour} from '../../theme';
import {CornerPosition} from '../../utils/constants';

import type {IconProp} from '@fortawesome/fontawesome-svg-core';
import type {FontAwesomeIconProps} from '@fortawesome/react-fontawesome';

const PANEL_PADDING = '20px';

type PanelButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  IconXorIcons & {
    active?: boolean;
  };
export const PanelButton = ({
  ref,
  icon,
  icons,
  active,
  ...props
}: PanelButtonProps & {
  ref?: React.RefObject<HTMLButtonElement>;
}) => {
  const iconElement = icon ? <Icon icon={icon} /> : null;
  const composedElement = icons ? <ComposedIcon icons={icons} /> : null;

  const handleKey = useCallback<
    (e: React.KeyboardEvent<HTMLButtonElement>) => void
  >((e) => {
    e.nativeEvent.stopPropagation();
    e.nativeEvent.preventDefault();
  }, []);

  return (
    <PanelButtonInner
      type="button"
      onKeyPress={handleKey}
      onKeyDown={handleKey}
      onKeyUp={handleKey}
      onBlur={(e) => e.stopPropagation()}
      $active={active}
      {...props}
      ref={ref}
    >
      {iconElement || composedElement}
    </PanelButtonInner>
  );
};
PanelButton.displayName = 'PanelButton';

export const PanelButtonInner = styled.button<{$active?: boolean}>`
  background-color: ${scToTwColour('theme.colours.slate1')};
  color: ${scToTwColour('theme.colours.slate12')};
  padding: 0.5em;
  font-size: 1rem;

  &:hover {
    background-color: ${scToTwColour('theme.colours.blue10')};
    color: ${scToTwColour('theme.colours.white')};
  }

  &[disabled],
  &[disabled]:hover {
    background-color: ${scToTwColour('theme.colours.slate1')};
    background-image: none;
    color: ${scToTwColour('theme.colours.slate12')};
    cursor: default;
  }

  ${({$active}) =>
    $active &&
    css`
      &,
      &[disabled],
      &[disabled]:hover {
        background-color: ${scToTwColour('theme.colours.blue10')};
        color: ${scToTwColour('theme.colours.white')};
      }
    `}
`;

type PanelExpandableProps = {
  icon: FontAwesomeIconProps['icon'];
  onClose?: () => void;
  active?: boolean;
  children: React.ReactNode;
};
export function PanelExpandable(
  props: PanelExpandableProps
): React.JSX.Element {
  const [open, setOpen] = useState(false);

  const icon = !open ? props.icon : faTimes;

  const handleClick = (): void => {
    open && props.onClose && props.onClose();
    setOpen(!open);
  };

  return (
    <Expandable>
      <PanelButton
        icon={icon}
        active={open || props.active}
        onClick={handleClick}
      />

      {open && (
        <ExpandableGroup
          onBlur={(e) => {
            const shouldClose = !e.currentTarget.contains(
              e.relatedTarget as HTMLElement
            );
            shouldClose && setOpen(false);
          }}
        >
          {props.children}
        </ExpandableGroup>
      )}
    </Expandable>
  );
}
const Expandable = styled.div`
  position: relative;
`;
const ExpandableGroup = styled.div`
  position: absolute;
  top: 50%;
  right: 100%;
  z-index: 10;
  margin-right: 0.5rem;
  max-height: 100%;
  overflow: hidden;
  border-radius: 0.4em;
  background-color: ${scToTwColour('theme.colours.slate1')};
  transform: translateY(-50%);
`;

const PanelGroupInner = styled.div`
  display: inline-flex;
  flex-flow: column nowrap;
  align-items: center;
  margin-bottom: 0.5rem;
  border-radius: 0.4rem;
  position: relative;

  &:last-of-type {
    margin-bottom: 0;
  }

  & > *:first-child,
  & > *:first-child > *:first-child:not(._icon) {
    border-top-left-radius: 0.4rem;
    border-top-right-radius: 0.4rem;
  }
  & > *:last-child,
  & > *:last-child > *:first-child:not(._icon) {
    border-bottom-left-radius: 0.4rem;
    border-bottom-right-radius: 0.4rem;
  }
`;
export const PanelGroup = ({className, ...props}: any) => (
  <PanelGroupInner {...props} className={clsx(className, 'shadow-md')} />
);

type ContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  quadrant: CornerPosition;
  children: React.ReactNode;
  status?: React.ReactNode;
  isSleeveOpen?: boolean;
};
export function PanelQuadrantContainer({
  quadrant,
  children,
  status,
  isSleeveOpen,
  className,
  ...props
}: ContainerProps): React.JSX.Element {
  return (
    <Container
      $quadrant={quadrant}
      $isSleeveOpen={isSleeveOpen}
      className={className}
    >
      {status}
      <PanelButtonsContainer $hasStatus={!!status} {...props}>
        {children}
      </PanelButtonsContainer>
    </Container>
  );
}
const PanelButtonsContainer = styled.div<{$hasStatus: boolean}>`
  display: flex;
  flex-flow: column nowrap;
  margin-left: ${({$hasStatus}) => ($hasStatus ? '0.5rem' : '0')};
`;
const Container = styled.div<{
  $quadrant: CornerPosition;
  $isSleeveOpen?: boolean;
}>`
  position: absolute;
  ${({$quadrant}) => getPositionProperties($quadrant)};

  display: ${({$isSleeveOpen}) => ($isSleeveOpen ? 'none' : 'flex')};
  align-items: ${({$quadrant}) =>
    $quadrant === CornerPosition.TopLeft ||
    $quadrant === CornerPosition.TopRight
      ? 'flex-start'
      : 'flex-end'};
  flex-direction: ${({$quadrant}) =>
    $quadrant === CornerPosition.TopLeft ||
    $quadrant === CornerPosition.BottomLeft
      ? 'row-reverse'
      : 'row'};

  width: auto;
  height: auto;

  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;
const getPositionProperties = (quadrant: ContainerProps['quadrant']): any => {
  switch (quadrant) {
    case CornerPosition.TopLeft:
      return css`
        top: ${PANEL_PADDING};
        left: ${PANEL_PADDING};
      `;
    case CornerPosition.TopRight:
      return css`
        top: ${PANEL_PADDING};
        right: ${PANEL_PADDING};
      `;
    case CornerPosition.BottomRight:
      return css`
        bottom: ${PANEL_PADDING};
        right: ${PANEL_PADDING};
      `;
    case CornerPosition.BottomLeft:
      return css`
        bottom: ${PANEL_PADDING};
        left: ${PANEL_PADDING};
      `;

    default:
      return css`
        top: ${PANEL_PADDING};
        right: ${PANEL_PADDING};
      `;
  }
};

type IconXorIcons =
  | {
      icon: IconProp;
      icons?: never;
    }
  | {
      icon?: never;
      icons: FontAwesomeIconProps[];
    };
